import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Star, Sparkles, Send, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { ModuleNotConnected, useRest, restFetch, RCard, RHead, RestLoading, RestPageBrief, SourceBadge, BRAND, BrandMark } from './restui';
import { AgentHelp } from '../../components/AgentHelp';

/* Reviews — Google / Yelp / Tripadvisor with AI suggested replies and autopilot. */

function Stars({ n, size = 13 }) {
  return (
    <span className="inline-flex gap-0.5" aria-label={`${n}/5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} style={{ width: size, height: size }} className={i <= n ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-200'} />
      ))}
    </span>
  );
}

function ReviewCard({ r, isEs, onReplied }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const sug = r.suggestion ? (isEs ? r.suggestion.es : r.suggestion.en) : '';
  const platformLabel = (BRAND[r.platform] || {}).label || r.platform;
  const send = async (t) => {
    setBusy(true);
    try {
      const resp = await restFetch(`/reviews/${r.id}/reply`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text: t }) });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      toast.success(isEs ? `Respuesta publicada en ${platformLabel} ✓` : `Reply published on ${platformLabel} ✓`);
      onReplied();
    } catch (e) {
      toast.error(String(e.message));
    } finally {
      setBusy(false);
    }
  };
  return (
    <RCard className="p-4 sm:p-5" testid={`review-card-${r.id}`}>
      <div className="flex items-start gap-3">
        <BrandMark k={r.platform} size={18} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <p className="text-[13.5px] font-bold text-gray-900">{r.author}</p>
            <Stars n={r.rating} />
            <span className="text-[11px] text-gray-400">{platformLabel} · {r.date}</span>
          </div>
          <p className="mt-1.5 text-[13px] text-gray-700 leading-relaxed">{r.text}</p>

          {r.replied ? (
            <div className="mt-3 rounded-xl bg-emerald-50 border border-emerald-200 px-3.5 py-2.5" data-testid={`review-reply-${r.id}`}>
              <p className="text-[10.5px] uppercase tracking-wider font-bold text-emerald-700">
                ✓ {isEs ? 'Respondida por Kitxens AI' : 'Replied by Kitxens AI'}{r.via === 'kitxens_ai_autopilot' ? ' · Autopilot' : ''}
              </p>
              <p className="mt-1 text-[12.5px] text-emerald-900 leading-snug">{r.reply_text}</p>
            </div>
          ) : (
            <div className="mt-3 rounded-xl bg-[var(--rest-accent-soft)] border border-[var(--rest-accent)]/25 px-3.5 py-2.5" data-testid={`review-suggestion-${r.id}`}>
              <p className="text-[11px] font-bold text-[var(--rest-accent)] inline-flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> {isEs ? 'Respuesta sugerida por Rachel' : 'Rachel suggested reply'} <AgentHelp agent="rachel" isEs={isEs} />
              </p>
              {editing ? (
                <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3}
                  className="mt-2 w-full rounded-lg border border-gray-200 p-2.5 text-[12.5px] text-gray-800 focus:outline-none focus:border-[var(--rest-accent)]"
                  data-testid={`review-edit-input-${r.id}`} />
              ) : (
                <p className="mt-1 text-[12.5px] text-gray-700 leading-snug">{sug}</p>
              )}
              <div className="mt-2.5 flex flex-wrap gap-2">
                <button onClick={() => send(editing ? text : sug)} disabled={busy || (editing && !text.trim())}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[var(--rest-accent)] text-white font-bold text-[12px] px-4 py-1.5 hover:opacity-90 active:scale-95 transition disabled:opacity-50"
                  data-testid={`review-send-${r.id}`}>
                  <Send className="w-3 h-3" /> {busy ? '…' : (isEs ? 'Publicar respuesta' : 'Publish reply')}
                </button>
                {!editing && (
                  <button onClick={() => { setEditing(true); setText(sug); }}
                    className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 text-gray-600 font-semibold text-[12px] px-3.5 py-1.5 hover:border-gray-300 transition-colors"
                    data-testid={`review-edit-${r.id}`}>
                    <Pencil className="w-3 h-3" /> {isEs ? 'Editar' : 'Edit'}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </RCard>
  );
}

export default function RestReviews() {
  const { isEs } = useOutletContext();
  const { data: d, loading, reload } = useRest('/reviews');
  const [apBusy, setApBusy] = useState(false);
  if (loading && !d) return <RestLoading />;
  if (!d?.has_data) {
    return <ModuleNotConnected isEs={isEs} title="Reviews" testid="rest-module-reviews"
      desc_en="Google & Yelp reviews, sentiment analysis and suggested responses." desc_es="Reseñas de Google y Yelp, análisis de sentimiento y respuestas sugeridas."
      needs={['Google Business Profile API', 'Yelp listing']} />;
  }
  const s = d.summary;
  const maxDist = Math.max(...Object.values(s.distribution || { 1: 1 }), 1);
  const toggleAutopilot = async () => {
    setApBusy(true);
    try {
      const r = await restFetch('/reviews/autopilot', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !d.autopilot, lang: isEs ? 'es' : 'en' }) });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      if (j.enabled && j.auto_replied > 0) {
        toast.success(isEs ? `Autopilot activado — Rachel respondió ${j.auto_replied} reseñas pendientes ✓` : `Autopilot on — Rachel replied to ${j.auto_replied} pending reviews ✓`);
      } else {
        toast.success(j.enabled ? (isEs ? 'Auto-respuesta activada' : 'Auto-reply enabled') : (isEs ? 'Auto-respuesta desactivada' : 'Auto-reply disabled'));
      }
      reload();
    } catch (e) {
      toast.error(String(e.message));
    } finally {
      setApBusy(false);
    }
  };
  return (
    <div data-testid="rest-reviews">
      <RHead title="Reviews"
        sub={isEs ? 'Google, Yelp y Tripadvisor en un solo lugar — con respuestas de AI listas para publicar.' : 'Google, Yelp and Tripadvisor in one place — with AI replies ready to publish.'}
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={toggleAutopilot} disabled={apBusy} data-testid="reviews-autopilot-toggle"
              className={`h-9 px-4 rounded-full text-[12.5px] font-bold transition-colors ${d.autopilot
                ? 'bg-emerald-50 border border-emerald-300 text-emerald-700'
                : 'bg-[var(--rest-accent)] text-white hover:opacity-90'}`}>
              {apBusy ? '…' : d.autopilot ? `● Autopilot ON` : (isEs ? '⚡ Activar auto-respuesta' : '⚡ Enable auto-reply')}
            </button>
            <SourceBadge source="connected_api" isEs={isEs} />
          </div>
        } />

      <RestPageBrief isEs={isEs} testid="rest-reviews-brief"
        summary={isEs
          ? `Tu valoración media es ${s.avg}★ en ${s.count} reseñas entre Google, Yelp y Tripadvisor. Últimos 30 días: ${s.avg_30d}★. ${s.pending > 0 ? `${s.pending} reseñas esperan respuesta.` : 'Todas las reseñas están respondidas.'}`
          : `Your average rating is ${s.avg}★ across ${s.count} reviews on Google, Yelp and Tripadvisor. Last 30 days: ${s.avg_30d}★. ${s.pending > 0 ? `${s.pending} reviews await a reply.` : 'All reviews are answered.'}`}
        alert={s.delta < -0.05 ? (isEs
          ? `La valoración bajó de ${s.avg_prev}★ a ${s.avg_30d}★ en 30 días — responde las negativas hoy para recuperarla.`
          : `Rating dropped from ${s.avg_prev}★ to ${s.avg_30d}★ in 30 days — answer the negatives today to recover.`) : null}
        kpi={{ label: isEs ? 'Valoración media' : 'Average rating', value: `${s.avg}★`, sub: `${s.count} ${isEs ? 'reseñas' : 'reviews'}`, down: s.delta < 0 }} />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4" data-testid="reviews-platforms">
        {s.by_platform.map((p) => (
          <RCard key={p.platform} className="p-4 flex items-center gap-3" testid={`reviews-platform-${p.platform}`}>
            <BrandMark k={p.platform} size={22} />
            <div>
              <p className="text-[13px] font-bold text-gray-900">{(BRAND[p.platform] || {}).label || p.platform}</p>
              <p className="text-[12px] text-gray-500 tabular-nums"><b className="text-gray-900 text-[15px]">{p.avg}★</b> · {p.count} {isEs ? 'reseñas' : 'reviews'}</p>
            </div>
          </RCard>
        ))}
      </div>

      <RCard className="p-4 sm:p-5 mb-5" testid="reviews-distribution">
        <p className="text-[11px] uppercase tracking-[0.15em] text-gray-400 font-bold mb-3">{isEs ? 'Distribución' : 'Distribution'}</p>
        <div className="space-y-1.5">
          {[5, 4, 3, 2, 1].map((n) => (
            <div key={n} className="flex items-center gap-3">
              <span className="w-8 text-[12px] font-semibold text-gray-600 tabular-nums">{n}★</span>
              <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                <div className={`h-full rounded-full kx-grow-x ${n >= 4 ? 'bg-emerald-400' : n === 3 ? 'bg-amber-400' : 'bg-red-400'}`}
                  style={{ width: `${Math.max(2, 100 * (s.distribution[String(n)] || 0) / maxDist)}%` }} />
              </div>
              <b className="w-8 text-right text-[12px] tabular-nums text-gray-600">{s.distribution[String(n)] || 0}</b>
            </div>
          ))}
        </div>
      </RCard>

      <div className="space-y-3" data-testid="reviews-list">
        {d.reviews.map((r) => (
          <ReviewCard key={r.id} r={r} isEs={isEs} onReplied={reload} />
        ))}
      </div>
    </div>
  );
}
